import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';

export interface LoanFundSelectProps {
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
}

/**
 * Loan Fund select component.
 * Fetches loan funds from the 'loanFund' provider.
 *
 * Used for parameter type: LOAN_FUND_SELECT
 */
const LoanFundSelect: React.FC<LoanFundSelectProps> = ({
  disabled,
  placeholder,
  ...rest
}) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const params = useMemo(() => ({
    search,
    fieldName: 'loanFund',
    entityType: 'REPORT',
  }), [search]);

  const { data: options, isLoading, error } = useGetSelectFieldValues('loanFund', params as any, {
    query: {
      staleTime: 5 * 60 * 1000,
    },
  });

  const selectOptions = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    return list.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
    }));
  }, [options]);

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      loading={isLoading}
      placeholder={placeholder || 'All Loan Funds'}
      options={selectOptions}
      filterOption={false}
      onSearch={(q) => setSearch(q)}
      notFoundContent={error ? 'Failed to load loan funds' : isLoading ? 'Loading...' : 'No loan funds'}
      optionFilterProp="label"
      {...rest}
    />
  );
};

export default LoanFundSelect;
